import type { FunctionComponent } from 'react';

import type { UseFormReturnType } from '@mantine/form';

import { Checkbox, Group, NumberInput, SegmentedControl, Select, Stack, Text } from '@mantine/core';

import { useTranslation } from 'i18n';

const NS = 'menuManagementEditByBulk';

const UPDATE_TYPE = {
  cents: 'cents',
  percentage: 'percentage',
};

type BulkFormValues = {
  allItems: boolean;
  allModifiers: boolean;
  requiredChange: string;
  updateBy: string;
  updateType: string;
};

// The form has no `transform`, so the transform generic is the default identity function.
type FormType = UseFormReturnType<BulkFormValues, (values: BulkFormValues) => BulkFormValues>;

export const BulkForm: FunctionComponent<{ form: FormType }> = ({ form }) => {
  const { t } = useTranslation(NS);
  const formValues = form.getValues();
  const isPercentage = formValues.updateType === UPDATE_TYPE.percentage;

  return (
    <Stack gap="lg">
      <Stack gap="sm">
        <Text variant="sublabel-sm">{t('BulkForm.updateMenuPricesLabel')}</Text>
        <Group gap="md">
          <Checkbox
            label={<Text variant="label-sm">{t('BulkForm.allItemsCheckboxLabel')}</Text>}
            key={form.key('allItems')}
            {...form.getInputProps('allItems', { type: 'checkbox' })} // eslint-disable-line react/jsx-props-no-spreading
          />
          <Checkbox
            label={<Text variant="label-sm">{t('BulkForm.allModifiersCheckboxLabel')}</Text>}
            key={form.key('allModifiers')}
            {...form.getInputProps('allModifiers', { type: 'checkbox' })} // eslint-disable-line react/jsx-props-no-spreading
          />
        </Group>
      </Stack>
      <Select
        data={[
          { value: 'increase', label: t('BulkForm.increasingAmountOption') },
          { value: 'decrease', label: t('BulkForm.decreasingAmountOption') },
        ]}
        label={t('BulkForm.updateByLabel')}
        placeholder={t('BulkForm.selectOptionsPlaceholder')}
        allowDeselect={false}
        size="md"
        key={form.key('updateBy')}
        {...form.getInputProps('updateBy')} // eslint-disable-line react/jsx-props-no-spreading
      />
      <Group gap="xs" align="flex-end" wrap="nowrap">
        <NumberInput
          label={t('BulkForm.updateAmountLabel')}
          placeholder={
            isPercentage ? t('BulkForm.percentagePlaceholder') : t('BulkForm.dollarPlaceholder')
          }
          prefix={isPercentage ? '' : '$'}
          suffix={isPercentage ? '%' : ''}
          allowDecimal
          decimalScale={2}
          fixedDecimalScale={!isPercentage}
          mb={0}
          rightSectionProps={{
            onClick: () => form.setFieldValue('requiredChange', ''),
            style: {
              display: form.getValues().requiredChange ? undefined : 'none',
            },
          }}
          style={{ flexGrow: 1 }}
          key={form.key('requiredChange')}
          {...form.getInputProps('requiredChange')} // eslint-disable-line react/jsx-props-no-spreading
        />
        <SegmentedControl
          size="sm"
          w={120}
          data={[
            { value: UPDATE_TYPE.percentage, label: t('BulkForm.percentageSegmentLabel') },
            { value: UPDATE_TYPE.cents, label: t('BulkForm.centsSegmentLabel') },
          ]}
          key={form.key('updateType')}
          {...form.getInputProps('updateType')} // eslint-disable-line react/jsx-props-no-spreading
        />
      </Group>
    </Stack>
  );
};
